/**
 * /maps — where things are.
 *
 * ★★ What this page used to be: eleven terminals at invented Shanghai
 *    coordinates (31.2304N, 121.4737E), four geofences with写好的 descriptions
 *    ("三楼玻璃幕墙区…激光雷达容易产生镜面反射"), six named places, five coverage
 *    percentages, and per-device trajectories — every one of them a literal in
 *    src/mocks/mapData.ts. The banner said the map "要换成 Leaflet + 天地图",
 *    which framed the problem as "the basemap is a placeholder" when the truth was
 *    that the fleet had never reported a position to the cloud at all.
 *
 * What made this page possible is a real change underneath, not a nicer canvas:
 * the dog's telemetry now carries its POSE (poseX/poseY/poseYaw + mapId), gated on
 * SLAM being alive, with its own timestamp so a stale pose cannot be dressed up as
 * a current one. Before today the hub knew CPU and temperature but not where any
 * device was — so no honest map of positions could exist.
 *
 * Still no Earth basemap, deliberately: indoor poses live in a SLAM map frame whose
 * origin is wherever the dog booted, and overlaying that on 天地图/高德 requires a
 * surveyed anchor per site that nobody has measured. design-fleet-console §6c is
 * explicit — without the anchor, do not overlay. So we draw the frame that is real,
 * in metres, and say so.
 */
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useTerminals, useKnowledge, useMapGridList, useMapGridImage, useLivePoses } from '@/hooks/useHubData';
import { NoDataSource, LoadError } from '@/components/feature/DataSource';
import MapFrameView, { type MapPlace, type MapObject, type MapDevice, type MapGridLayer } from './components/MapFrameView';
import DevicePanel from './components/DevicePanel';
import DispatchPanel from './components/DispatchPanel';
import { useTasks } from '@/hooks/useHubData';

// 3D 视图懒加载:three.js 有 ~170KB(gz), 不该让不看 3D 的页面替它买单。
const Map3D = lazy(() => import('./components/Map3D'));

type PanelTab = 'devices' | 'dispatch' | 'places' | 'objects' | 'zones';

/** 栅格是"多久以前的地图" —— 和位姿一样, 值要和值的时间一起出现。 */
function gridAge(updatedAt: number): string {
  const s = Math.max(0, Date.now() / 1000 - updatedAt);
  if (s < 90) return `${Math.round(s)} 秒`;
  if (s < 5400) return `${Math.round(s / 60)} 分钟`;
  return `${(s / 3600).toFixed(1)} 小时`;
}

export default function MapsPage() {
  const { terminals, loading: devLoading, error: devError, refetch } = useTerminals();
  const kg = useKnowledge();
  const [rightPanel, setRightPanel] = useState<PanelTab>('devices');
  const [mapId, setMapId] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ x: number; y: number } | null>(null);
  const [view3d, setView3d] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const livePoses = useLivePoses();
  const { tasks, refetch: refetchTasks } = useTasks();
  const openTasks = tasks.filter((t) => t.status === 'pending' || t.status === 'in_progress');

  /**
   * 本次会话的轨迹。★ 规格 §⑤ 要"轨迹", 而 hub 不存历史 —— 能诚实给出的只有
   * 【打开这一页之后】从实时位姿流攒的这一段, 刷新即清零。页面上明说这一点,
   * 免得有人把它当成今天走过的全部路线(规格里的"时空回放"标着 ⬜, 就是因为缺归档)。
   */
  const trailsRef = useRef<Record<string, { x: number; y: number }[]>>({});
  const [trailTick, setTrailTick] = useState(0);
  useEffect(() => {
    let changed = false;
    for (const p of Object.values(livePoses)) {
      const arr = trailsRef.current[p.deviceId] ?? (trailsRef.current[p.deviceId] = []);
      const last = arr[arr.length - 1];
      // 只在真的挪动了才记点:站着不动时的保活包不该堆成一坨。
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 0.05) {
        arr.push({ x: p.x, y: p.y });
        if (arr.length > 4000) arr.shift();   // 有界:一条轨迹不该无限长
        changed = true;
      }
    }
    if (changed) setTrailTick((t) => t + 1);
  }, [livePoses]);

  // 位姿随遥测每分钟更新一次 —— 页面必须跟着重取, 否则打开那一刻的位置会一直挂在
  // 屏幕上, 而它每过一分钟就更旧一点。20 秒重取一次, 只是一个小 JSON。
  useEffect(() => {
    const t = setInterval(refetch, 20_000);
    return () => clearInterval(t);
  }, [refetch]);

  const graph = kg.graph;
  // 底图元信息先拿:一张刚建、只有栅格还没有任何地点物体的新图, 也得能被选到
  // —— 否则它会因为"没有散点"而根本不出现在选择器里。
  const gridMetas = useMapGridList();

  /** Every map id that has something in it. Coordinates from two different maps
   *  are not comparable (each SLAM map has its own origin), so the page shows
   *  exactly one at a time rather than a merged scatter that looks like a room. */
  const mapIds = useMemo(() => {
    const s = new Set<string>();
    graph?.places.forEach(p => p.mapId && s.add(p.mapId));
    graph?.objects.forEach(o => o.mapId && s.add(o.mapId));
    terminals.forEach(t => t.poseMapId && s.add(t.poseMapId));
    (gridMetas ?? []).forEach(g => s.add(g.mapId));
    return [...s].sort();
  }, [graph, terminals, gridMetas]);

  const activeMap = mapId && mapIds.includes(mapId) ? mapId : mapIds[0] ?? null;

  const { grid: gridData } = useMapGridImage(activeMap, gridMetas);
  const gridLayer: MapGridLayer | null = gridData
    ? {
        bitmap: gridData.bitmap,
        resolution: gridData.meta.resolution,
        originX: gridData.meta.originX,
        originY: gridData.meta.originY,
        updatedAt: gridData.meta.updatedAt,
        known: gridData.meta.known,
      }
    : null;

  const places: MapPlace[] = useMemo(() => (graph?.places ?? [])
    .filter(p => p.mapId === activeMap)
    .map(p => ({ name: p.name, x: p.x, y: p.y, deviceId: p.deviceId, descr: p.descr })), [graph, activeMap]);

  const objects: MapObject[] = useMemo(() => (graph?.objects ?? [])
    .filter(o => o.mapId === activeMap)
    .map(o => ({ name: o.name, x: o.x, y: o.y, nObs: o.nObs, deviceId: o.deviceId, lastSeen: o.lastSeen })), [graph, activeMap]);

  /** Only devices whose pose belongs to the map being shown. A device on another
   *  map is not "somewhere off screen" — its coordinates mean nothing here. */
  /**
   * 每台设备取【一条】位姿:有实时流就用实时的, 没有就退回遥测里那条。
   *
   * ★ 两者都带各自的时间戳, 而地图按时间戳决定画成实心(此刻)还是空心(最后已知)。
   *   合成时**不许**把遥测那条的时间戳安到实时流上 —— 那就等于给旧位置发了张新证。
   */
  const devices: MapDevice[] = useMemo(() => terminals
    .map(t => {
      const live = livePoses[t.id];
      if (live && live.mapId === activeMap) {
        return { id: t.id, name: t.name, x: live.x, y: live.y, yaw: live.yaw,
          poseAt: live.at / 1000, online: t.status === 'online', live: true };
      }
      if (t.poseX != null && t.poseY != null && t.poseMapId === activeMap) {
        return { id: t.id, name: t.name, x: t.poseX, y: t.poseY, yaw: t.poseYaw,
          poseAt: t.poseAt ?? 0, online: t.status === 'online', live: false };
      }
      return null;
    })
    .filter((d): d is MapDevice & { live: boolean } => d !== null), [terminals, livePoses, activeMap]);

  const anyLive = devices.some(d => (d as MapDevice & { live?: boolean }).live);

  /** Registered devices that have never sent a pose — named, not silently absent. */
  const posedIds = new Set(terminals.filter(t => t.poseX != null).map(t => t.id));
  const withoutPose = terminals.filter(t => !posedIds.has(t.id) && !t.revoked);

  if (devError) return <div className="p-4 lg:p-6"><LoadError error={devError} onRetry={refetch} /></div>;

  return (
    <div className="p-4 lg:p-6 flex flex-col min-h-[calc(100vh-4rem)]">
      <div className="animate-fade-in-up">
        <h1 className="text-lg font-bold text-foreground-900 font-heading mb-1">指挥舱</h1>
        <p className="text-xs text-foreground-500 mb-3 lg:mb-4">
          全队位置、命名地点与记忆中的物体, 画在设备自己那张图的 <span className="font-mono">map</span> 帧里(单位:米);
          右栏可按地点派任务(走台账, 设备自己认领执行)。
          没有经纬度底图 —— 室内位姿的原点是开机那一刻的位置,叠到世界地图上需要场地锚点标定,而锚点还没测。
        </p>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        <div className="flex items-center gap-0.5 bg-background-100 rounded-lg p-0.5">
          {[{ v: false, label: '平面', icon: 'ri-map-2-line' }, { v: true, label: '3D', icon: 'ri-box-3-line' }].map(m => (
            <button key={String(m.v)} onClick={() => setView3d(m.v)}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md cursor-pointer whitespace-nowrap transition-all ${
                view3d === m.v ? 'bg-background-50 text-foreground-800 border border-background-200/70 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${m.icon} text-xs`} /></span>
              {m.label}
            </button>
          ))}
        </div>
        <span className={`inline-flex items-center gap-1.5 ml-1 text-[11px] ${anyLive ? 'text-accent-600' : 'text-foreground-400'}`}
          title={anyLive
            ? '设备正在以约 2 Hz 推送当前位姿(站着不动时 5 秒一次保活)'
            : '没有实时位姿流 —— 画的是遥测里那条"最后已知位置", 每分钟一次'}>
          <span className={`w-1.5 h-1.5 rounded-full ${anyLive ? 'bg-accent-500 animate-pulse' : 'bg-foreground-300'}`} />
          {anyLive ? '实时位姿' : '无实时流 · 用遥测位姿'}
        </span>
      </div>

      {mapIds.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          <span className="text-[11px] text-foreground-400 mr-1">图:</span>
          {mapIds.map(m => (
            <button key={m} onClick={() => { setMapId(m); setFocus(null); }}
              title="不同的图各有各的原点, 坐标不可跨图比较"
              className={`px-2.5 py-1 rounded-md text-[11px] font-mono cursor-pointer whitespace-nowrap transition-all ${
                m === activeMap ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'}`}>
              {m}
            </button>
          ))}
        </div>
      )}

      {kg.unavailable && (
        <NoDataSource
          title="知识汇聚未接通, 地图上只能有设备位姿"
          reason={kg.unavailable}
        />
      )}

      {!kg.loading && !devLoading && mapIds.length === 0 ? (
        <NoDataSource
          title="还没有任何带坐标的东西"
          reason="设备要么还没推送过知识(地点/物体), 要么还没上报过位姿。位姿随遥测每分钟一次, 且只在设备定位活着时才带 —— 定位不在时宁可没有, 也不报一个旧的。"
        />
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row gap-3 lg:gap-4 min-h-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex-1 flex flex-col min-w-0 min-h-[420px] lg:min-h-[60vh]">
            {view3d ? (
              <Suspense fallback={<div className="flex-1 min-h-[420px] rounded-lg border border-background-200/70 bg-[#0b1020] flex items-center justify-center text-xs text-foreground-400">载入 3D 视图…</div>}>
                <Map3D places={places} objects={objects} devices={devices} grid={gridLayer} />
              </Suspense>
            ) : (
              <MapFrameView
                places={places} objects={objects} devices={devices} grid={gridLayer} focus={focus}
                trails={trailsRef.current} selectedDeviceId={selectedDevice}
                onSelectDevice={setSelectedDevice}
                key={`t${trailTick}`}
              />
            )}
            <p className="text-[11px] text-foreground-400 mt-1.5 leading-relaxed">
              {gridLayer
                ? `底图是设备推上来的占据栅格(${(gridLayer.known * 100).toFixed(0)}% 已观测, ${gridAge(gridLayer.updatedAt)}前), 深色=障碍、浅色=已走通、中灰=没看过。`
                : '这张图还没收到过占据栅格 —— 只有点没有墙。设备侧 gridsync 每 3 分钟推一次(有变化才推)。'}
              {anyLive
                ? '设备位姿走实时流(约 2 Hz, 只在定位活着时推;站着不动时 5 秒一次保活)。'
                : '此刻没有实时流, 画的是遥测里的位姿(每分钟一次)。'}
              空心 + "N 分钟前" = 那是【最后已知位置】, 不是它此刻在哪
              (2026-08-08 有过定位门闩死 44 分钟而其余一切正常的先例)。
              {withoutPose.length > 0 && (
                <> 还没报过位姿的设备:{withoutPose.map(t => t.name).join('、')}。</>
              )}
            </p>
          </div>

          <div className="w-full lg:w-72 flex-shrink-0 flex flex-col min-h-0">
            <div className="flex border-b border-background-200/70 mb-3">
              {([
                { key: 'devices' as const, label: `设备 ${devices.length}`, icon: 'ri-robot-line' },
                { key: 'dispatch' as const, label: '派任务', icon: 'ri-send-plane-line' },
                { key: 'places' as const, label: `地点 ${places.length}`, icon: 'ri-map-pin-line' },
                { key: 'objects' as const, label: `物体 ${objects.length}`, icon: 'ri-box-3-line' },
                { key: 'zones' as const, label: '禁区', icon: 'ri-alert-line' },
              ]).map(tab => (
                <button key={tab.key} onClick={() => setRightPanel(tab.key)}
                  className={`flex items-center gap-1 px-3 py-2 text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    rightPanel === tab.key ? 'text-foreground-900 border-b-2 border-primary-500' : 'text-foreground-400 hover:text-foreground-600'}`}>
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className={`${tab.icon} text-xs`} /></span>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto">
              {rightPanel === 'devices' && (
                <DevicePanel terminals={terminals} devices={devices} livePoses={livePoses}
                  selected={selectedDevice} onSelect={setSelectedDevice}
                  gridKnown={gridLayer?.known ?? null} />
              )}
              {rightPanel === 'dispatch' && (
                <div className="space-y-4">
                  <DispatchPanel terminals={terminals} places={places} mapId={activeMap}
                    presetPlace={null} onDispatched={refetchTasks} />
                  <div>
                    <h4 className="text-[11px] font-semibold text-foreground-700 mb-1.5">进行中的任务</h4>
                    {openTasks.length === 0 ? (
                      <p className="text-[10px] text-foreground-400">台账里没有未完成的任务。</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {openTasks.map((t) => (
                          <li key={t.id} className="p-2 rounded-md bg-background-50 border border-background-200/70">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] text-foreground-800 truncate">{t.name}</span>
                              <span className="text-[10px] font-mono text-foreground-400">{t.progress}%</span>
                            </div>
                            <div className="h-1 bg-background-200 rounded-full mt-1 overflow-hidden">
                              <div className="h-full bg-primary-500 rounded-full" style={{ width: `${t.progress}%` }} />
                            </div>
                            {/* 进度是【数出来的】(done/total 单元), 不是任务行自报的字段。 */}
                            <p className="text-[10px] text-foreground-400 mt-0.5">
                              {t.assignees.length ? `认领:${t.assignees.join('、')}` : '还没有设备认领'}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
              {rightPanel === 'places' && (
                places.length === 0
                  ? <p className="text-[11px] text-foreground-400 py-6 text-center">这张图上还没有命名地点。</p>
                  : <ul className="space-y-1.5">
                      {places.map(p => (
                        <li key={`${p.deviceId}-${p.name}`}>
                          <button onClick={() => setFocus({ x: p.x, y: p.y })}
                            className="w-full text-left p-2.5 rounded-md bg-background-50 border border-background-200/70 hover:border-primary-300/60 cursor-pointer transition-all">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-medium text-foreground-800 truncate">{p.name}</span>
                              <span className="text-[10px] font-mono text-foreground-400 shrink-0">
                                {p.x.toFixed(1)}, {p.y.toFixed(1)}
                              </span>
                            </div>
                            {p.descr && <p className="text-[10px] text-foreground-400 mt-0.5 line-clamp-2">{p.descr}</p>}
                          </button>
                        </li>
                      ))}
                    </ul>
              )}

              {rightPanel === 'objects' && (
                objects.length === 0
                  ? <p className="text-[11px] text-foreground-400 py-6 text-center">这张图上还没有记下物体。</p>
                  : <ul className="space-y-1">
                      {/* 按观测次数排序:看得多的更可能是真的, 这也是圆点大小的依据。 */}
                      {[...objects].sort((a, b) => b.nObs - a.nObs).map((o, i) => (
                        <li key={`${o.deviceId}-${o.name}-${i}`}>
                          <button onClick={() => setFocus({ x: o.x, y: o.y })}
                            className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-background-100 cursor-pointer transition-colors">
                            <span className="text-[11px] text-foreground-700 truncate">{o.name}</span>
                            <span className="text-[10px] font-mono text-foreground-400 shrink-0">
                              {o.nObs}次 · {o.x.toFixed(1)}, {o.y.toFixed(1)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
              )}

              {rightPanel === 'zones' && (
                <NoDataSource
                  title="禁区没有数据源"
                  reason={'原型里那四个禁区(玻璃幕墙区/施工区域/服务器机房/室外斜坡)连描述都是编的。hub 没有禁区表, ' +
                    '狗上也还没有"云端下发的禁区"这条通路 —— 真要有, 它得能在断网时仍然生效, 那属于运维下发(P4)。'}
                  phase="P4 · 配置下发"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
